import {
  useState,
  useEffect,
} from 'react';
import {
  ShoppingCart,
  Calendar,
  MapPin,
  Clock,
  DollarSign,
  TrendingDown,
  Users,
  Gavel,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type SourcingEvent,
  SOURCING_STATUS_LABELS,
  SOURCING_STATUS_COLORS,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
} from '@/types/sourcing';

interface SourcingEventCardProps {
  readonly event: SourcingEvent;
  readonly onSubmitBid?: (eventId: string) => void;
  readonly onViewDetails?: (eventId: string) => void;
  readonly hasBid?: boolean;
  readonly myBidAmount?: number;
}

interface AuctionState {
  isReverseAuction: boolean;
  isOpen: boolean;
  isAuctionLive: boolean;
  isAuctionUpcoming: boolean;
  isWinning: boolean;
  isOutbid: boolean;
}

function getAuctionLabel(isAuctionLive: boolean, isAuctionUpcoming: boolean): string {
  if (isAuctionLive) return 'Live Auction';
  if (isAuctionUpcoming) return 'Upcoming Auction';
  return 'Auction';
}

function getBidStatusClass(isWinning: boolean, isOutbid: boolean): string {
  if (isWinning) return 'text-green-600';
  if (isOutbid) return 'text-orange-600';
  return '';
}

function useAuctionTimer(event: SourcingEvent): string {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (event.eventType !== 'reverse_auction') return;

    const updateTimer = () => {
      const now = Date.now();
      const endTime = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
      const startTime = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;

      if (now < startTime) {
        const diff = startTime - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`Starts in ${hours}h ${mins}m`);
      } else if (now < endTime) {
        const diff = endTime - now;
        const mins = Math.floor(diff / (1000 * 60));
        const secs = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')} remaining`);
      } else {
        setTimeRemaining('Auction ended');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [event.eventType, event.auctionStartTime, event.auctionEndTime]);

  return timeRemaining;
}

function useAuctionState(event: SourcingEvent, myBidAmount?: number): AuctionState {
  const isReverseAuction = event.eventType === 'reverse_auction';
  const isOpen = event.status === 'open';
  const now = Date.now();
  const auctionStart = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;
  const auctionEnd = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
  const isAuctionLive = isReverseAuction && now >= auctionStart && now < auctionEnd;
  const isAuctionUpcoming = isReverseAuction && now < auctionStart;
  const isWinning = myBidAmount !== undefined && event.currentLowestBid === myBidAmount;
  const isOutbid = myBidAmount !== undefined && event.currentLowestBid !== undefined && myBidAmount > event.currentLowestBid;

  return {
    isReverseAuction,
    isOpen,
    isAuctionLive,
    isAuctionUpcoming,
    isWinning,
    isOutbid,
  };
}

interface ReverseAuctionSectionProps {
  readonly event: SourcingEvent;
  readonly state: AuctionState;
  readonly timeRemaining: string;
  readonly myBidAmount?: number;
  readonly formatCurrency: (amount: number) => string;
  readonly formatTime: (dateStr: string) => string;
}

function ReverseAuctionSection({
  event,
  state,
  timeRemaining,
  myBidAmount,
  formatCurrency,
  formatTime,
}: ReverseAuctionSectionProps) {
  return (
    <div className={`rounded-lg p-3 ${state.isAuctionLive ? 'bg-orange-50 border border-orange-200' : 'bg-muted/50'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium flex items-center gap-1">
          <TrendingDown className="h-4 w-4" />
          {getAuctionLabel(state.isAuctionLive, state.isAuctionUpcoming)}
        </span>
        {timeRemaining && (
          <span className={`text-sm font-mono ${state.isAuctionLive ? 'text-orange-600 font-bold' : 'text-muted-foreground'}`}>
            {timeRemaining}
          </span>
        )}
      </div>
      
      {event.currentLowestBid !== undefined && (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Current Lowest Bid</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(event.currentLowestBid)}</p>
          </div>
          {event.totalBids !== undefined && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total Bids</p>
              <p className="text-lg font-semibold flex items-center gap-1">
                <Users className="h-4 w-4" />
                {event.totalBids}
              </p>
            </div>
          )}
        </div>
      )}

      {event.startingPrice && !event.currentLowestBid && (
        <div>
          <p className="text-xs text-muted-foreground">Starting Price</p>
          <p className="text-lg font-semibold">{formatCurrency(event.startingPrice)}</p>
        </div>
      )}

      {myBidAmount !== undefined && (
        <div className={`mt-2 pt-2 border-t ${state.isWinning ? 'border-green-200' : 'border-orange-200'}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm">Your Bid:</span>
            <span className={`font-semibold ${getBidStatusClass(state.isWinning, state.isOutbid)}`}>
              {formatCurrency(myBidAmount)}
              {state.isWinning && ' ✓ Winning'}
              {state.isOutbid && ' ⚠ Outbid'}
            </span>
          </div>
        </div>
      )}

      {event.auctionStartTime && event.auctionEndTime && (
        <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
          <Clock className="h-3 w-3 inline mr-1" />
          {formatTime(event.auctionStartTime)} - {formatTime(event.auctionEndTime)}
        </div>
      )}
    </div>
  );
}

interface SealedProposalSectionProps {
  readonly event: SourcingEvent;
  readonly hasBid: boolean;
  readonly formatDate: (dateStr: string) => string;
  readonly formatTime: (dateStr: string) => string;
}

function SealedProposalSection({
  event,
  hasBid,
  formatDate,
  formatTime,
}: SealedProposalSectionProps) {
  return (
    <div className="rounded-lg p-3 bg-indigo-50 border border-indigo-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Proposal Deadline</p>
          <p className="font-semibold">{formatDate(event.bidDeadline)} at {formatTime(event.bidDeadline)}</p>
        </div>
        {event.totalBids !== undefined && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Proposals</p>
            <p className="font-semibold">{event.totalBids}</p>
          </div>
        )}
      </div>
      {hasBid && (
        <div className="mt-2 pt-2 border-t border-indigo-200">
          <span className="text-sm text-indigo-700">✓ Proposal Submitted</span>
        </div>
      )}
    </div>
  );
}

export function SourcingEventCard({
  event,
  onSubmitBid,
  onViewDetails,
  hasBid = false,
  myBidAmount,
}: SourcingEventCardProps) {
  const timeRemaining = useAuctionTimer(event);
  const state = useAuctionState(event, myBidAmount);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: event.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const showViewDetailsFullWidth = !state.isAuctionLive && !(!state.isReverseAuction && state.isOpen && !hasBid);

  return (
    <Card className={`hover:shadow-md transition-shadow ${state.isAuctionLive ? 'ring-2 ring-orange-500' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${state.isReverseAuction ? 'bg-orange-100' : 'bg-primary/10'}`}>
              {state.isReverseAuction ? (
                <Gavel className="h-5 w-5 text-orange-600" />
              ) : (
                <ShoppingCart className="h-5 w-5 text-primary" />
              )}
            </div>
            <div>
              <CardTitle className="text-base">{event.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{event.eventNumber}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${EVENT_TYPE_COLORS[event.eventType]}`}>
              {EVENT_TYPE_LABELS[event.eventType]}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${SOURCING_STATUS_COLORS[event.status]}`}>
              {SOURCING_STATUS_LABELS[event.status]}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.isReverseAuction && (
          <ReverseAuctionSection
            event={event}
            state={state}
            timeRemaining={timeRemaining}
            myBidAmount={myBidAmount}
            formatCurrency={formatCurrency}
            formatTime={formatTime}
          />
        )}

        {!state.isReverseAuction && (
          <SealedProposalSection
            event={event}
            hasBid={hasBid}
            formatDate={formatDate}
            formatTime={formatTime}
          />
        )}

        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{event.deliveryZone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>Deliver by {formatDate(event.requiredDeliveryDate)}</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span>Est. {formatCurrency(event.estimatedValue)}</span>
          </div>
        </div>

        <div className="border-t pt-3">
          <p className="text-sm text-muted-foreground mb-2">{event.lines.length} line items</p>
          <div className="flex flex-wrap gap-1">
            {[...new Set(event.lines.map((l) => l.category))].slice(0, 3).map((cat) => (
              <span key={cat} className="rounded bg-muted px-2 py-0.5 text-xs">
                {cat}
              </span>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          {state.isReverseAuction && state.isAuctionLive && onSubmitBid && (
            <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={() => onSubmitBid(event.eventId)}>
              <Gavel className="h-4 w-4 mr-2" />
              {hasBid ? 'Lower Your Bid' : 'Place Bid'}
            </Button>
          )}
          
          {!state.isReverseAuction && state.isOpen && !hasBid && onSubmitBid && (
            <Button className="flex-1" onClick={() => onSubmitBid(event.eventId)}>
              Submit Proposal
            </Button>
          )}

          {state.isReverseAuction && state.isAuctionUpcoming && event.auctionStartTime && (
            <Button variant="outline" className="flex-1" disabled>
              <Clock className="h-4 w-4 mr-2" />
              Starts {formatTime(event.auctionStartTime)}
            </Button>
          )}

          {onViewDetails && (
            <Button
              variant="outline"
              className={showViewDetailsFullWidth ? 'flex-1' : ''}
              onClick={() => onViewDetails(event.eventId)}
            >
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
